/** @jsxImportSource @emotion/react */
import React, { ReactElement, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Slide, Snackbar, SnackbarOrigin } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import Alert from '@mui/material/Alert';

import { useStyles } from './PopupAlert.styles';
import { is } from '../../utils';
import { PopupAlertState, selectAllPopupAlerts } from '../../store/reducers';
import { PopupAlertType } from '../../constants';

export interface State extends SnackbarOrigin {
  open: boolean;
  alert: PopupAlertState;
  Transition: React.ComponentType<
    TransitionProps & { children?: ReactElement }
  >;
}

function SlideTransition({ children, ...props }: TransitionProps) {
  return (
    <Slide {...props} direction="down" children={children as ReactElement} />
  );
}

export function PopupAlert(): React.ReactElement {
  const styles = useStyles();
  const alerts = useSelector(selectAllPopupAlerts);

  const [state, setState] = React.useState<State>({
    open: false,
    vertical: 'top',
    horizontal: 'center',
    Transition: SlideTransition,
    alert: { variant: PopupAlertType.Info, content: '' },
  });

  const { vertical, horizontal, open, alert } = state;

  const handleClose = () => {
    setState({ ...state, open: false });
  };

  useEffect(() => {
    if (is.empty(alerts.content)) return;

    setState({
      ...state,
      open: true,
      alert: alerts,
    } as State);
  }, [alerts]);

  return (
    <Snackbar
      css={styles}
      data-test-id="snackbar-container"
      anchorOrigin={{ vertical, horizontal }}
      open={open}
      onClose={handleClose}
      key={state.Transition.name}
      autoHideDuration={20000}
      TransitionComponent={state.Transition}
    >
      <Alert severity={alert.variant} onClose={handleClose}>
        {alert.content}
      </Alert>
    </Snackbar>
  );
}
